import { useRef, useState } from 'react';
import { ApiError } from '../api/client';
import { aresLookup } from '../api/hooks';
import type { AresContact } from '../api/types';
import { Btn, Field } from './ui';

/** IČO na osm číslic – API kratší nebere, mezery se ignorují. Nesmysl vrátí ''. */
function normalizeIco(raw: string): string {
  const digits = raw.replace(/\s/g, '');
  return /^\d{1,8}$/.test(digits) ? digits.padStart(8, '0') : '';
}

/**
 * Pole pro IČO s tlačítkem „Načíst z ARES“ – jediné místo, kde se v appce
 * sahá na ARES. Komponenta je řízená, hodnotu drží formulář kolem ní;
 * načtená data předá přes onFound a formulář si je rozstrčí do svých polí.
 */
export function AresLookup({
  value,
  onChange,
  onFound,
  onBlur,
  error,
  label = 'IČO',
  autoLookup = true,
}: {
  value: string;
  onChange: (ico: string) => void;
  onFound: (c: AresContact) => void;
  /** volitelně: formulář si při opuštění pole uloží ručně zadané IČO */
  onBlur?: () => void;
  error?: string;
  label?: string;
  /** načíst samo po opuštění pole s platným osmimístným IČO */
  autoLookup?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState('');
  /** poslední úspěšně načtené IČO – stejné znovu netaháme */
  const [done, setDone] = useState('');
  const running = useRef(false);

  const ico = normalizeIco(value);
  const loaded = !!ico && ico === done;

  const lookup = async () => {
    if (running.current || loaded) return;
    if (!ico) {
      setFailed('Zadej IČO, 8 číslic');
      return;
    }
    running.current = true;
    setBusy(true);
    setFailed('');
    let found: AresContact | null = null;
    try {
      found = await aresLookup(ico);
    } catch (err) {
      setFailed(err instanceof ApiError ? err.message : 'ARES neodpovídá');
    }
    running.current = false;
    setBusy(false);
    if (found) {
      setDone(found.ico);
      onFound(found);
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
      <div style={{ flex: '1 1 180px', minWidth: 0 }}>
        <Field
          label={label}
          inputMode="numeric"
          autoComplete="off"
          placeholder="8 číslic"
          value={value}
          onChange={(e) => {
            setFailed('');
            onChange(e.target.value);
          }}
          onBlur={() => {
            onBlur?.();
            if (autoLookup && /^\d{8}$/.test(value.replace(/\s/g, ''))) void lookup();
          }}
          onKeyDown={(e) => {
            // Enter v IČO hledá v ARES, neodesílá formulář kolem
            if (e.key === 'Enter') {
              e.preventDefault();
              void lookup();
            }
          }}
          error={failed || error}
        />
      </div>
      <div className="field" style={{ flex: '0 0 auto' }}>
        {/* prázdný popisek drží tlačítko v jedné lince s polem */}
        <span className="k" aria-hidden="true">
          &nbsp;
        </span>
        <Btn sm loading={busy} disabled={loaded} onClick={() => void lookup()}>
          Načíst z ARES
        </Btn>
      </div>
    </div>
  );
}
